import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, shouldRenderMobile, TokenUtils } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { pollingService } from '@philips-emr/hpms-provider-commons/lib/services/polling/polling-service';
import TermsService from '@philips-emr/hpms-provider-commons/lib/services/terms-service';
import { HPMSOnOptionClick } from '@philips-emr/hpms-provider-components/lib/events/event';
import { AvatarAPI, DropDownMenuAPI, FetchAPI, LinkAPI, NotificationCenterAPI, PanelAPI, RepeaterAPI, TextAPI } from '@philips-emr/tws-components';
import { ThemeTonalRange, Type } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnClick, OnMount } from '@philips-emr/tws-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { AuthenticationService, HttpService, SessionStorageService, ThemeService } from '@philips-emr/tws-core/lib/services';
import { applicationContext } from '@philips-emr/tws-foundation';
import { foundationContext } from '@philips-emr/tws-foundation/lib/context';

import { ProviderAliases as Aliases, FragmentNames } from '../../codegen';
import { FOUNDATION_ROUTES, Route } from '../../route';
import BaseController from '../BaseController';
import { Message } from '../messages/messages-controller';

import './provider-controller.css';

enum NotificationStatus {
  NO = 'N',
  YES = 'S',
}

interface Notification {
  dtCreation: string,
  nrSequence: string,
  dsTitle: string,
  dsText: string,
  dtNotification: string,
  ieRead: NotificationStatus,
  unread: number;
}

enum NOTIFICATION_CLICK_TYPE {
  NOTIFICATION = 'NotificationClick',
  MARK_AS_READ = 'MarkAsRead',
  MARK_ALL_AS_READ = 'MarkAllAsRead',
}

const typeAccreditation = 'tipo-credenciamento';
const schedule = 'agendamentos';
const inconsistency = 'inconsistencias';
const forms = 'formularios';
const proposal = 'proposta';
const contracts = 'contratos';
const details = 'detalhes';
const pollingTimeNotification = 120000;
const pollingTimeMessage = 300000;

@Controller({ fragment: FragmentNames.PROVIDER })
class ProviderController extends BaseController {
  @Inject(Aliases.REPEATER_PROVIDER)
  private repeaterProvider: RepeaterAPI;
  @Inject(Aliases.DROP_DOWN_PROVIDERS)
  private dropDownProviders: DropDownMenuAPI;
  @Inject(Aliases.TEXT_DS_PROVIDER)
  private textDsProvider: TextAPI;
  @Inject(Aliases.TEXT_NR_CNPJ)
  private textCNPJ: TextAPI;
  @Inject(Aliases.TEXT_ADDRESS)
  private textAddress: TextAPI;
  @Inject(Aliases.TEXT_DS_ADDRESS)
  private textDsAddress: TextAPI;
  @Inject(Aliases.PANEL_TEXT_PROFILE)
  private panelTextProfile: PanelAPI;
  @Inject(Aliases.TEXT_PROFILE)
  private textProfile: TextAPI;
  @Inject(Aliases.PANEL_DROPDOWN_PROVIDERS)
  private panelDropdownProviders: PanelAPI;
  @Inject(Aliases.NOTIFICATION_CENTER)
  private notificationCenter: NotificationCenterAPI;
  @Inject(Aliases.FETCH_NOTIFICATIONS)
  private fetchNotification: FetchAPI;
  @Inject(Aliases.LINK_MESSAGE)
  private linkMessage: LinkAPI;

  private shouldRenderMargins(path: string): boolean {

    return path.includes(typeAccreditation) || path.includes(schedule)
      || path.includes(inconsistency) || path.includes(forms) || path.includes(proposal)
      || (path.includes(contracts) && path.includes(details));
  }

  @OnMount([Aliases.PANEL_ACCREDITATION, Aliases.PANEL_APP_ROUTES])
  onMountMain(event: MountEvent): void {
    const api = event.target as PanelAPI;
    this.validatePanel(api, false);
    this.historyService.addListener((path: string) => {
      this.controlProviderList(path);
      if (this.shouldRenderMargins(path)) {
        api.setVisible(api.getAlias() == Aliases.PANEL_ACCREDITATION);
      } else {
        catchRenderException(() => {
          api.setVisible(api.getAlias() != Aliases.PANEL_ACCREDITATION);
        });
      }
      return Promise.resolve();
    });
  }

  private validatePanel(api: PanelAPI, visible: boolean): void {
    if (this.shouldRenderMargins(this.historyService.getCurrentRoute())) {
      api.setVisible(visible);
    } else {
      catchRenderException(() => {
        api.setVisible(!visible);
      });
    }
  }

  @OnMount(Aliases.LAYOUT_PROVIDER)
  onMount(): void {
    const { nrSeqWSuiteUser, nrSeqPlsUser } = TokenUtils.getValues();
    this.context.set({ nrSeqWSuiteUser, nrSeqPlsUser });
    ThemeService.setContentTonalRange(ThemeTonalRange.ULTRA_LIGHT);
    foundationContext.setShowEstablishment(false);
    foundationContext.setShowSector(false);
    TermsService.checkTerms();
  }

  @OnMount(Aliases.PANEL_PROFILES)
  OnMountDropDown(): void {
    catchRenderException(() => {
      this.controlProviderList(FOUNDATION_ROUTES.DASHBOARD);
      this.setSelectedProviderData();
    });
  }

  @OnMount(Aliases.PANEL_PROVIDERS)
  onPanelContratosMount(): void {
    this.controlProviderList(FOUNDATION_ROUTES.DASHBOARD);
  }

  @OnMount(Aliases.AVATAR_USER)
  onMountAvatar(event: MountEvent): void {

    const api = event.target as AvatarAPI;
    const user = foundationContext.getUser();
    if (user) {
      let username = user.name;
      if (username.length > 30) {
        username = username.substring(0, 30);
        username = `${username}${'...'}`;
      }

      if (!shouldRenderMobile()) {
        api.setTitle(username);
      }
    }
  }

  @OnClick(Aliases.OPTION_EXIT)
  onExitClick(): void {
    const { ssoLogin } = TokenUtils.getValues();
    const { ssoLogoutRoute } = TokenUtils.getValues();

    pollingService.removePolling('notification');
    pollingService.removePolling('message');
    this.context.set({ messagesUnreads: undefined });
    void AuthenticationService.logout();

    if (ssoLogin == 'S') {
      this.historyService.redirect(ssoLogoutRoute);
      return;
    }
    applicationContext.redirect(FOUNDATION_ROUTES.LOGIN);
  }

  @OnClick(Aliases.OPTION_MY_DATA)
  onClickMeusDados(): void {
    this.historyService.push(Route.MY_DATA);
  }

  @HPMSOnOptionClick(Aliases.HPMS_PROVIDER_OPTIONS)
  onOptionClick(event: ClickEvent): void {
    const data = event.value;
    const { nrSeqProvider } = data;

    providerContext.setSelectedProvider(nrSeqProvider);

    this.context.set({ nrSeqProvider });
    SessionStorageService.removeItem('nrSeqProvider');
    SessionStorageService.setItem('nrSeqProvider', nrSeqProvider);

    if (this.historyService.getCurrentRoute() == FOUNDATION_ROUTES.DASHBOARD) {
      this.historyService.push(FOUNDATION_ROUTES.HOME);
    }

    this.historyService.push(FOUNDATION_ROUTES.DASHBOARD);
    this.dropDownProviders.close();
    this.setSelectedProviderData();
  }

  @OnMount(Aliases.NOTIFICATION_CENTER)
  onMountNotificacionCenter(): void {
    pollingService.createPolling('notification', this.notificationsUnreads.bind(this), pollingTimeNotification);
    this.notificationsUnreads();
  }

  @OnAfterFetch(Aliases.FETCH_NOTIFICATIONS)
  onAfterFetchNotification(fetchEvent: FetchEvent): void {
    const notifications = fetchEvent.data as Notification[];
    const notificationFormattedData = notifications.map((data, index) => {
      const isNewPage = index >= (notifications.length - 15);

      if (!isNewPage) {
        return data;
      }

      const isRead = data.ieRead === 'S';

      return {
        date: data.dtNotification,
        description: data.dsText,
        id: data.nrSequence,
        read: isRead,
        title: data.dsTitle,
        type: Type.NOTICE,
      };
    });

    this.fetchNotification.setState({ data: notificationFormattedData });
  }

  @OnClick(Aliases.NOTIFICATION_CENTER)
  onNotificationClick(event: ClickEvent): void {
    switch (event.value.type) {
      case NOTIFICATION_CLICK_TYPE.MARK_ALL_AS_READ:
        this.handleMarkAllAsReadClick();
        break;
      case NOTIFICATION_CLICK_TYPE.MARK_AS_READ:
        this.handleNotificationMarkAsReadClick(event);
        break;
    }
  }

  private setSelectedProviderData(): void {
    const selectedProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const providers = providerContext.getProviders();
    SessionStorageService.removeItem('nrSeqProvider');

    catchRenderException(() => {
      providers.forEach(provider => {
        const nmProvider = shouldRenderMobile() && provider.nmProvider.length > 34
          ? provider.nmProvider.slice(0, 34).concat('...') : provider.nmProvider;
        if (selectedProvider == provider.nrSeqProvider) {
          this.context.set({ nrSeqProvider: provider.nrSeqProvider });
          catchRenderException(() => this.textDsProvider.setValue(nmProvider));
          catchRenderException(() => this.textCNPJ.setValue(provider.cdCpfCgcProvider));
          catchRenderException(() => this.textDsAddress.setValue(provider.address));
          catchRenderException(() => this.textProfile.setValue(provider.nmProvider));
          if (!provider.address) {
            this.textAddress.setValue('');
          }
        }
        this.controlProviderList(FOUNDATION_ROUTES.DASHBOARD);

      });
    });
  }

  private controlProviderList(path: string): void {
    const currentProvider = providerContext.getSelectedProvider();
    const data: Record<string, Value>[] = [];
    const providers = providerContext.getProviders();
    catchRenderException(() => {
      providers.forEach(provider => {
        if (provider.nrSeqProvider !== currentProvider.nrSeqProvider) {
          data.push({
            title: provider.nmProvider,
            subtitle: provider.cdCpfCgcProvider,
            nrSeqProvider: provider.nrSeqProvider,
          });
        }
      });

      if (providers.length == 1 || path != FOUNDATION_ROUTES.DASHBOARD) {
        this.panelTextProfile.setVisible(true);
        this.panelDropdownProviders.setVisible(false);
        setTimeout(() => {
          catchRenderException(() => { this.textProfile.setValue(currentProvider.nmProvider); });
        }, 100);
      } else {
        this.panelTextProfile.setVisible(false);
        this.panelDropdownProviders.setVisible(true);
        this.textDsProvider.setValue(currentProvider.nmProvider);
      }
    });

    catchRenderException(() => {
      this.repeaterProvider.setData(data);
    });
  }

  public notificationsUnreads(): void {
    void HttpService.get(Endpoint.GET.NOTIFICATIONS_UNREAD).then(response => {
      const { body } = response;
      const { unread } = body as Notification;
      catchRenderException(() => this.notificationCenter.setUnreadNotificationsCount(unread));
    });
  }

  handleMarkAllAsReadClick(): void {
    void HttpService.post(Endpoint.POST.NOTIFICATION_MARK_ALL_AS_READ, {}).then(() => {
      this.notificationCenter.markAllAsRead();
      this.notificationsUnreads();
    });
  }

  private handleNotificationMarkAsReadClick(event: ClickEvent): void {
    const { id } = event.value.item;
    this.markNotificationAsRead(id);
  }

  private markNotificationAsRead(nrSequence: number): void {
    void HttpService.post(Endpoint.POST.NOTIFICATION_MARK_AS_READ, { nrSequence }, {}).then(() => {
      this.notificationCenter.setReadStatus(nrSequence, true);
      const qtdUnread = this.notificationCenter.getUnreadNotificationCount();
      this.notificationCenter.setUnreadNotificationsCount(qtdUnread - 1);
    });
  }

  @OnMount(Aliases.LINK_MESSAGE)
  onMountFragmentMessages(): void {
    pollingService.createPolling('message', this.messagesUnreads.bind(this), pollingTimeMessage);
    this.messagesUnreads();
    this.context.set({ messagesUnreads: this.messagesUnreads.bind(this) });
  }

  public messagesUnreads(): void {
    HttpService.get(Endpoint.GET.MESSAGES_UNREAD).then(response => {
      const { body } = response;
      const { qtUnread } = body as Message;
      this.linkMessage.setBadge(String(qtUnread));
    }).catch(() => {
      catchRenderException(() => this.linkMessage.setBadge(''));
    });
  }

  @OnClick([Aliases.LINK_SHOW_ALL_MESSAGES, Aliases.OPTION_MESSAGES])
  onClickLinkShowAllMessages = (): void => this.historyService.push(Route.MESSAGES);
}
export { ProviderController };
